import { toast } from 'react-toastify';
import { InputField, NXForm } from '../../common';
import { useEffect, useState } from 'react';
import useApi from '../../../api';
import NxtbnDrawer from '../../drawer';
import SelectStyled from '../../Select';
import enumChoice, { getEnumItem, makeEnumFriendly } from '../../../enum';
import moment from 'moment';
import { useDispatch, useSelector } from 'react-redux';
import { fetchWarehouse } from '../../../redux/warehosueSlice';
import { RootState } from '../../../redux/rootReducer';
import ProductWithVariantSearch from '../../common/productWithVariantSearchField';

interface PurchaseCreateProps {
    isOpen: boolean;
    onClose: () => void;
    refresh: () => void;
    id?: number;
}

function PurchaseCreate({ isOpen, onClose, refresh, id }: PurchaseCreateProps) {
    const [formData, setFormData] = useState<any>({ items: [{}] });
    const [errorData, setErrorData] = useState<any>({});
    const [suppliers, setSuppliers] = useState<any>();
    const warehouses = useSelector((state: RootState) => state.warehouse);

    const api = useApi();
    const dispatch = useDispatch();

    const getSuppliers = () => {
        api.getSuppliers()
            .then((response: any) => {
                setSuppliers(response.results);
            })
            .catch(() => toast.error('Error fetching supplier.'));
    };

    const getWarehouses = () => {
        dispatch(fetchWarehouse() as any);
    };

    useEffect(() => {
        if (isOpen) {
            getSuppliers();
            getWarehouses();
        }
    }, [isOpen]);

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();

        api.createPurchase(formData).then(
            () => {
                refresh();
                toast.success("Supplier Created Successfully!");
                onClose();
                setFormData({ items: [{}] });
            },
            (error) => {
                setErrorData(error.response.data);
            }
        );
    };

    const handleChange = (e: any) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value,
        });
    };

    const handleSelectChange = (name: any, value: any) => {
        setFormData((prev: any) => ({ ...prev, [name]: value }));
    };

    const onVariantChange = (variantData: any[]) => {
        const updatedVariantData = variantData.map((item) => ({
            ...item,
            variant: item.dbId,
            ordered_quantity: item.quantity,
        }));
        setFormData((prev: any) => ({ ...prev, items: updatedVariantData }));
    };


    return (
        <NxtbnDrawer
            isOpen={isOpen}
            onClose={() => {
                onClose();
                setFormData({ items: [{}] });
            }}
            title={id ? 'Edit Purchase Order' : 'Create Purchase Order'}
        >
            <NXForm onSubmit={handleSubmit} className="p-6" title="Create Purchase Order">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                    <div>
                        <label htmlFor="supplier" className="block text-sm font-medium text-gray-700">
                            Supplier
                        </label>
                        <SelectStyled
                            name="supplier"
                            errorData={errorData}
                            defaultValue={getEnumItem(suppliers, formData?.supplier)}
                            options={makeEnumFriendly(suppliers || [])}
                            onChange={(e) => handleSelectChange('supplier', e.value)}
                            className="mt-1"
                        />
                    </div>

                    <div>
                        <label htmlFor="destination" className="block text-sm font-medium text-gray-700">
                            Destination
                        </label>
                        <SelectStyled
                            name="destination"
                            errorData={errorData}
                            defaultValue={getEnumItem(warehouses.results, formData?.destination)}
                            options={makeEnumFriendly(warehouses.results || [])}
                            onChange={(e) => handleSelectChange('destination', e.value)}
                            className="mt-1"
                        />
                    </div>

                    <div>
                        <label htmlFor="expected_delivery_date" className="block text-sm font-medium text-gray-700">
                            Estimated Arrival
                        </label>
                        <InputField
                            type="datetime-local"
                            id="expected_delivery_date"
                            name="expected_delivery_date"
                            errorData={errorData}
                            defaultValue={formData?.expected_delivery_date}
                            onChange={(e) => {
                                setFormData((prev: any) => ({
                                    ...prev,
                                    expected_delivery_date: moment(e.target.value).format('YYYY-MM-DD'),
                                }));
                            }}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>

                    <div>
                        <label htmlFor="total_cost" className="block text-sm font-medium text-gray-700">
                            Total Cost
                        </label>
                        <InputField
                            type="number"
                            id="total_cost"
                            name="total_cost"
                            errorData={errorData}
                            defaultValue={formData?.total_cost}
                            onChange={handleChange}
                            className="mt-1 block w-full border border-gray-300 rounded-md shadow-sm p-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        />
                    </div>
                </div>

                <div className="mb-6">
                    <ProductWithVariantSearch usedIn="purchase" onVariantChange={onVariantChange} />
                </div>

                <div className="flex justify-end">
                    <button
                        type="button"
                        onClick={onClose}
                        className="mr-3 px-4 py-2 text-[#0CAF60] border border-[#0CAF60] hover:bg-slate-200 text-sm font-medium rounded-md shadow-sm focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="inline-flex justify-center rounded-md border border-transparent px-4 py-2 bg-primary-600 text-base font-medium text-white shadow-sm hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm"
                    >
                        Save as Draft
                    </button>
                </div>
            </NXForm>
        </NxtbnDrawer>
    );
}

export default PurchaseCreate;
